import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { LogOut, Monitor, Moon, Search, Shield, Sun, User } from "lucide-react";
import { FeedbackButton } from "@/components/FeedbackButton";
import { Input } from "@/components/ui/Input";
import { signOut } from "@/lib/clerk";
import { cn } from "@/lib/cn";
import { useTheme } from "@/lib/theme";
import type { Me } from "@/lib/me";

interface Props {
  me: Me | null;
  query: string;
  onQueryChange: (q: string) => void;
}

const THEMES = [
  { value: "light", icon: Sun, label: "Light" },
  { value: "dark", icon: Moon, label: "Dark" },
  { value: "system", icon: Monitor, label: "System" },
] as const;

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  return (
    <div
      role="group"
      aria-label="Colour theme"
      className="border-border bg-card flex rounded-md border p-0.5"
    >
      {THEMES.map(({ value, icon: Icon, label }) => (
        <button
          key={value}
          type="button"
          onClick={() => setTheme(value)}
          aria-label={label}
          aria-pressed={theme === value}
          className={cn(
            "grid h-7 w-7 place-items-center rounded transition-colors",
            theme === value
              ? "bg-accent text-accent-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          <Icon size={14} />
        </button>
      ))}
    </div>
  );
}

const MENU_ITEM =
  "hover:bg-accent flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors";

/**
 * The account menu — and the only way to sign out.
 *
 * Everything here was previously either absent (sign-out) or a separate
 * control competing for header width (the theme switch). On a phone that left
 * the header wrapping onto three rows with the avatar stranded on its own; a
 * menu is one 32px target instead.
 */
function AccountMenu({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (wrapper.current && !wrapper.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  /**
   * ⚠ Clear the cache *after* Clerk reports the session gone, not before.
   * The session listener flips the app to the sign-in screen, which disables
   * the `me` query; clearing first would let it refetch without a token and
   * flash a "session ended" refusal on the way out. Clearing at all matters
   * because the next person to sign in on this browser must not see a frame
   * of the previous person's name and role.
   */
  const onSignOut = () => {
    setOpen(false);
    void signOut().then(() => queryClient.clear());
  };

  return (
    <div ref={wrapper} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="hover:bg-accent -mx-1 flex items-center gap-2 rounded-md px-1 py-1"
      >
        <div className="hidden text-right leading-tight sm:block">
          <div className="max-w-40 truncate text-sm font-medium">
            {me.name ?? me.email}
          </div>
          <div className="text-muted-foreground text-xs capitalize">{me.role}</div>
        </div>
        <span
          aria-hidden="true"
          className="bg-accent text-accent-foreground grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-semibold"
        >
          {initials(me.name, me.email)}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="border-border bg-card absolute right-0 z-30 mt-2 w-64 rounded-lg border p-1.5 shadow-lg"
        >
          {/* On a phone this is the only place the signed-in identity appears,
              since the header itself has no room for it. */}
          <div className="px-2.5 py-2">
            {/* Nobody needs their email printed twice: it stands in as the
                name when there isn't one, and sits under it when there is. */}
            <div className="truncate text-sm font-medium">{me.name ?? me.email}</div>
            {me.name && (
              <div className="text-muted-foreground truncate text-xs">{me.email}</div>
            )}
            <div className="text-muted-foreground mt-1 text-xs capitalize">{me.role}</div>
          </div>

          <div className="bg-border my-1 h-px" />

          <Link role="menuitem" to="/profile" onClick={() => setOpen(false)} className={MENU_ITEM}>
            <User size={15} /> Your profile
          </Link>

          <div className="px-2.5 pt-2 pb-1">
            <div className="text-muted-foreground mb-1.5 text-xs">Theme</div>
            <ThemeToggle />
          </div>

          <div className="bg-border my-1 h-px" />

          <button role="menuitem" type="button" onClick={onSignOut} className={MENU_ITEM}>
            <LogOut size={15} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function initials(name: string | null, email: string): string {
  const source = name?.trim() || email;
  const words = source.split(/[^A-Za-z0-9]+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return (words[0] ?? "").slice(0, 2).toUpperCase();
  return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? "")).toUpperCase();
}

export function Header({ me, query, onQueryChange }: Props) {
  return (
    <header className="border-border bg-background/85 sticky top-0 z-20 border-b backdrop-blur">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-4 py-3 sm:gap-3 sm:px-5">
        <Link to="/" className="flex min-w-0 items-center gap-2.5">
          <img
            src="/logo.svg"
            alt=""
            width={30}
            height={30}
            className="shrink-0 rounded-lg"
          />
          <span className="truncate font-semibold tracking-tight">Ley Choon Apps</span>
        </Link>

        {/*
          Below `sm` the search takes a row of its own (`order-last w-full`) —
          a phone has no width for a logo, a search field and the account
          controls side by side, and squeezing them made the header wrap into
          three ragged rows.
        */}
        <div className="relative order-last w-full sm:order-none sm:ml-auto sm:w-auto sm:max-w-xs sm:flex-1">
          <Search
            size={15}
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
          />
          <Input
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search apps"
            aria-label="Search apps"
            type="search"
            className="h-9 pl-9"
          />
        </div>

        {/* The account area, kept together so it stays flush right on a phone
            once the search field has dropped to its own row. */}
        <div className="ml-auto flex shrink-0 items-center gap-1 sm:ml-0 sm:gap-2">
          {me && <FeedbackButton />}

          {me?.isManager && (
            <Link
              to="/admin"
              aria-label="Administration"
              className="text-muted-foreground hover:text-foreground hover:bg-accent inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-sm"
            >
              <Shield size={15} />
              <span className="hidden sm:inline">Admin</span>
            </Link>
          )}

          {me && <AccountMenu me={me} />}
        </div>
      </div>
    </header>
  );
}
